import HashtagChip from '@components/dataDisplay/hashtag/HashtagChip';
import { cardCommonStyle } from '@styles/common/Card';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { FormConfig } from '@typings/form/form.interface';
import {
  FORM_FIELD_TYPE,
  FORM_STATUS,
  FORM_STATUS_MAP,
} from '@typings/form/formTypes';
import { useDocument } from '@nandorojo/swr-firestore';
import { ICampItem } from '@typings/photocard/camp.interface';
import { useRecoilValue } from 'recoil';
import { isMasterState, currentUserIDState } from '@atom/setting/user';
import { ACCESS_TYPE } from '@typings/common/accessType';
import icons from '@components/dataDisplay/Icons/Icons';

// QuillContents 제거 — 카드 목록에서는 경량 텍스트 렌더링 사용
const stripHtml = (html: string | undefined): string => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
};

const CardBox = styled.div`
  width: 100%;
  height: 100%;
  max-height: 200px;
  padding: 16px;
  ${cardCommonStyle}
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Thumbnail = styled.div`
  width: 80px;
  height: 120px;
  overflow: hidden;
  border-radius: var(--cardBorderRadius);
  background-color: var(--cardBackgroundColor);
  border: 1px solid var(--lineColor);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0; // 텍스트 오버플로우 방지
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--textColor);
  line-height: 1.3;

  // 텍스트 오버플로우 처리
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubTitle = styled.div`
  font-size: 12px;
  color: var(--subTextColor);
  line-height: 1.3;

  // 2줄까지만 표시
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AuthorText = styled.div`
  font-size: 11px;
  color: var(--subTextColor);
  margin-top: 4px;
`;

const PlaceholderThumbnail = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--backgroundColor);
  border: 1px solid var(--lineColor);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
`;

const MaskedCardBox = styled.div`
  width: 100%;
  height: 100%;
  max-height: 200px;
  ${cardCommonStyle}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const MaskedIconWrap = styled.div`
  opacity: 0.35;

  svg path {
    fill: var(--textColor);
  }
`;

type Props = {
  data: any; // FormResponse 타입이어야 하지만 현재는 any로 유지
  collectionPath: string;
  docLayout: string;
  formConfig?: FormConfig; // 🆕 formConfig 추가
  authorName?: string; // 부모에서 캐싱된 작성자 이름
  campMap?: Map<string, ICampItem>; // 부모에서 캐싱된 진영 Map
};

function FormThumbCard({ data, collectionPath, formConfig, authorName, campMap }: Props) {
  const isMaster = useRecoilValue(isMasterState);
  const currentUserID = useRecoilValue(currentUserIDState);

  const isSecret = data.accessType === ACCESS_TYPE.SECRET;
  const isAuthor = currentUserID === data.authorUid;
  const shouldMask = isSecret && !isMaster && !isAuthor;

  const docId = data.id || data.objectID;

  // Algolia 검색 결과는 responses가 없으므로 Firestore에서 fetch
  // responses가 있으면 null을 전달하여 fetch 생략
  const { data: firestoreDoc } = useDocument<any>(
    !data.responses && docId ? `${collectionPath}/${docId}` : null,
    { listen: false },
  );

  // Algolia hit에 responses가 없을 때만 Firestore 데이터로 보완
  const resolvedData = !data.responses && firestoreDoc
    ? { ...data, responses: firestoreDoc.responses, thumbnailUrl: firestoreDoc.thumbnailUrl, formConfigId: firestoreDoc.formConfigId ?? data.formConfigId }
    : data;

  // formConfig prop이 없을 때(검색 결과)만 formSetting에서 직접 fetch
  const resolvedFormConfigId = resolvedData.formConfigId || data.formConfigId;
  const { data: fetchedFormConfig } = useDocument<FormConfig>(
    !formConfig && resolvedFormConfigId ? `formSetting/${resolvedFormConfigId}` : null,
    { listen: false },
  );
  const effectiveFormConfig = formConfig || fetchedFormConfig || undefined;

  // 작성자 정보: 부모에서 캐싱된 authorName prop 사용 (개별 useDocument 제거)

  // CAMP_SELECT 타입 필드 ID 목록
  const campSelectFieldIds = useMemo(() => {
    if (!effectiveFormConfig?.fields) return new Set<string>();
    return new Set(
      effectiveFormConfig.fields
        .filter((f) => f.type === FORM_FIELD_TYPE.CAMP_SELECT)
        .map((f) => f.id),
    );
  }, [effectiveFormConfig?.fields]);

  const mapping = effectiveFormConfig?.listDisplayMapping;
  const responses = resolvedData.responses || {};

  // 각 매핑 필드가 CAMP_SELECT인 경우 campId 추출
  const subTitleCampId =
    mapping?.subTitleFieldId &&
      campSelectFieldIds.has(mapping.subTitleFieldId)
      ? (responses[mapping.subTitleFieldId]?.value as string) || null
      : null;
  const optionCampId =
    mapping?.optionFieldId && campSelectFieldIds.has(mapping.optionFieldId)
      ? (responses[mapping.optionFieldId]?.value as string) || null
      : null;
  const option2CampId =
    mapping?.optionField2Id && campSelectFieldIds.has(mapping.optionField2Id)
      ? (responses[mapping.optionField2Id]?.value as string) || null
      : null;
  // effectiveFormConfig 없을 때 resolvedData.camp 폴백도 campId로 간주
  const fallbackCampId = !effectiveFormConfig?.listDisplayMapping
    ? (resolvedData.camp as string) || null
    : null;

  // campId → campMap prop에서 조회 (개별 useDocument 제거)
  const subTitleCamp = subTitleCampId ? campMap?.get(subTitleCampId) : undefined;
  const optionCamp = optionCampId ? campMap?.get(optionCampId) : undefined;
  const option2Camp = option2CampId ? campMap?.get(option2CampId) : undefined;
  const fallbackCamp = fallbackCampId ? campMap?.get(fallbackCampId) : undefined;

  // listDisplayMapping을 기반으로 동적 값 추출
  const {
    displayTitle,
    displaySubTitle,
    displayThumbnail,
    displayOption,
    displayOption2,
  } = useMemo(() => {
    const toStr = (v: any): string => {
      if (!v) return '';
      if (Array.isArray(v)) return v[0] ? String(v[0]) : '';
      return String(v);
    };

    // formConfig가 없거나 listDisplayMapping이 없으면 기본값 사용
    if (!effectiveFormConfig?.listDisplayMapping || !resolvedData.responses) {
      return {
        displayTitle: resolvedData.title || '제목 없음',
        displaySubTitle:
          fallbackCamp?.title || resolvedData.subTitle || resolvedData.camp || '',
        displayThumbnail: resolvedData.thumbnailUrl || null,
        displayOption: null,
        displayOption2: null,
      };
    }

    const map = effectiveFormConfig.listDisplayMapping;
    const res = resolvedData.responses;

    const titleResponse = map.titleFieldId ? res[map.titleFieldId] : null;
    const thumbnailResponse = map.thumbnailFieldId
      ? res[map.thumbnailFieldId]
      : null;
    const subTitleResponse = map.subTitleFieldId
      ? res[map.subTitleFieldId]
      : null;
    const optionResponse = map.optionFieldId ? res[map.optionFieldId] : null;
    const option2Response = map.optionField2Id
      ? res[map.optionField2Id]
      : null;

    // 썸네일은 첫 번째 URL만 사용
    const rawThumb = thumbnailResponse?.value;
    const resolvedThumb = Array.isArray(rawThumb)
      ? rawThumb[0] || null
      : rawThumb || resolvedData.thumbnailUrl || null;

    // 부제목: '선택하지 않음'(null)이면 숨김, CAMP_SELECT이면 camp.title, 아니면 일반 값
    const resolvedSubTitle = map.subTitleFieldId === null
      ? ''
      : subTitleCampId
        ? subTitleCamp?.title || subTitleCampId
        : toStr(subTitleResponse?.value) || resolvedData.subTitle || resolvedData.camp || '';

    // 옵션 칩: CAMP_SELECT이면 camp.title, 아니면 일반 값
    const resolvedOption = optionCampId
      ? optionCamp?.title || optionCampId
      : optionResponse?.value || null;

    const resolvedOption2 = option2CampId
      ? option2Camp?.title || option2CampId
      : option2Response?.value || null;

    return {
      displayTitle: toStr(titleResponse?.value) || resolvedData.title || '제목 없음',
      displaySubTitle: resolvedSubTitle,
      displayThumbnail: resolvedThumb,
      displayOption: resolvedOption,
      displayOption2: resolvedOption2,
    };
  }, [
    resolvedData,
    effectiveFormConfig,
    subTitleCamp,
    optionCamp,
    option2Camp,
    fallbackCamp,
    subTitleCampId,
    optionCampId,
    option2CampId,
  ]);

  if (shouldMask) {
    return (
      <MaskedCardBox>
        <MaskedIconWrap>{icons['Lock18']}</MaskedIconWrap>
        <Title>비공개</Title>
      </MaskedCardBox>
    );
  }

  return (
    <CardBox>
      <CardHeader>
        {/* 썸네일 표시 */}
        {displayThumbnail ? (
          <Thumbnail>
            <img src={displayThumbnail} alt="thumbnail" />
          </Thumbnail>
        ) : (
          <PlaceholderThumbnail>📄</PlaceholderThumbnail>
        )}

        {/* 제목과 부제목 */}
        <ContentArea>
          <Title title={stripHtml(displayTitle)}>
            {stripHtml(displayTitle)}
          </Title>
          {displaySubTitle && (
            <SubTitle title={stripHtml(displaySubTitle)}>
              {stripHtml(displaySubTitle)}
            </SubTitle>
          )}
          {authorName && (
            <AuthorText>{authorName}</AuthorText>
          )}
          {/* 옵션 필드 표시 */}
          {(displayOption || displayOption2) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {displayOption && FORM_STATUS_MAP[displayOption] && (
                <HashtagChip
                  value={FORM_STATUS_MAP[displayOption]}
                  style={{
                    backgroundColor:
                      displayOption === FORM_STATUS.DRAFT
                        ? 'gray'
                        : 'var(--buttonBackgroundColor)',
                  }}
                />
              )}
              {displayOption && !FORM_STATUS_MAP[displayOption] && (
                <HashtagChip value={String(displayOption)} />
              )}
              {displayOption2 && FORM_STATUS_MAP[displayOption2] && (
                <HashtagChip
                  value={FORM_STATUS_MAP[displayOption2]}
                  style={{
                    backgroundColor:
                      displayOption2 === FORM_STATUS.DRAFT
                        ? 'gray'
                        : 'var(--buttonBackgroundColor)',
                  }}
                />
              )}
              {displayOption2 && !FORM_STATUS_MAP[displayOption2] && (
                <HashtagChip value={String(displayOption2)} />
              )}
            </div>
          )}
        </ContentArea>
      </CardHeader>
    </CardBox>
  );
}

export default FormThumbCard;
